import React, { useState, useEffect } from "react"
import _ from "lodash"
import { InputText } from "primereact/inputtext"
import { formatDate, isValidDate, parseDate } from "data/lib/dates"
import { localizeNum, tryParseFloat, isValidNumber } from "data/lib/numbers"

type DateString = string|null
type DateInputProps = {
  attrs?: Function
  data: object
  field: string|string[]
  format?: string
  persist: (value: DateString, field: string|string[]) => void
  withoutDays?: boolean
  className?: string
  allowRelative?: boolean
}

const format = (format, withoutDays) => s =>
  formatDate(parseDate(s), format, withoutDays)

const DateInput = (props: DateInputProps) => {
  const _format = format(
    props.withoutDays ? "MM/YYYY" : "MM/DD/YYYY",
    props.withoutDays
  )

  let placeholder = ""
  let initialValue = ""
  let propValue = _.get(props.data, props.field)
  if(propValue && isValidDate(propValue)) initialValue = _format(propValue)
  if(propValue && props.allowRelative && isValidNumber(propValue)) initialValue = localizeNum(propValue)
  if(props.withoutDays) {
    if(props.allowRelative) placeholder = "MM/YYYY or Months"
    else placeholder = "MM/YYYY"
  } else {
    if(props.allowRelative) placeholder = "MM/DD/YYYY or Months"
    else placeholder = "MM/DD/YYYY"
  }

  const [dirty, setDirty] = useState<boolean>(false)
  const [date, updateDate] = useState<string>(initialValue)
  const [lastGoodValue, updateLastGoodValue] = useState<string>(initialValue)

  function update(_e: React.FormEvent): void {
    if(props.allowRelative && isValidNumber(date)) {
      let parsedVal = tryParseFloat(date)
      props.persist(parsedVal, props.field)
      updateLastGoodValue(date)
    } else if(isValidDate(date)) {
      props.persist(format((props.format || "YYYY-MM-DD"), props.withoutDays)(date), props.field)
      const validDate = _format(date)
      updateDate(validDate)
      updateLastGoodValue(validDate)
    } else if(_.isNil(date) || date === '') {
      updateDate('')
      props.persist(null, props.field)
    } else {
      updateDate(lastGoodValue)
    }
    setDirty(false)
  }

  useEffect(() => {
    if (!initialValue && !_.isNil(date)){
      updateDate("")
    }
    if (!_.isNil(initialValue)){
      updateDate(initialValue);
    }
  }, [initialValue])

  const attrs = props.attrs && props.attrs(props.field) || {}

  const valid = (key) => /^(\w|-|\/)$/.test(key) || key === 'Delete' || key === 'Backspace'

  return (
    <InputText
      type="text"
      className={props.className}
      value={date}
      {...attrs}
      onChange={e => updateDate((e.target as HTMLInputElement).value)}
      onKeyDown={e => {
        if (!dirty && valid(e.key)) setDirty(true)
        switch(e.key) {
          case "Enter":
            updateDate((e.target as HTMLInputElement).value)
            break
          case "Tab":
            updateDate((e.target as HTMLInputElement).value)
            break
        }
      }}
      onBlur={e => dirty && update(e)}
      placeholder={placeholder}
    />
  )
}

export default DateInput
